// Live @hub/api AuditDataSource (HUB-512) — binds the reviewer workbench to the
// evalreview.v1 service. The blind-review invariant is enforced SERVER-SIDE (the
// service assigns opaque A/B slots and withholds provenance), so this source
// simply maps the already-blind wire shapes to the audit view types. Nothing
// here can reveal which slot is the agent.
//
// M1 (agent-only PDF path) limits: the coding API returns codes, not patient
// demographics, and the human-coder (B) side arrives with the FA leg (M2). So
// case identity is the DCN, demographic/record fields are empty, and the B slot
// is blank until FA. Those fields fill in as later milestones land — the shapes
// are already correct.
//
// PHI: coding results are shown to an authorized reviewer (the product) but must
// never be logged/persisted, and only case ids (never payloads) reach query
// keys/URLs — the AuditDataSource hooks already enforce this.

import { evalReviewService } from "@/core/api/services";
import { CodeCaseMode as WireCodeCaseMode } from "@hub/proto/evalreview/v1/evalreview_pb";
import type { AuditDataSource } from "../AuditDataSource";
import { faDate } from "../data/faDates";
import { comparableCode } from "../data/fields";
import type {
  AuditBatch,
  AuditBatchCaseSummary,
  AuditBatchState,
  AuditCase,
  AuditSubmission,
  CaseRecord,
  CaseStatus,
  CodebookHit,
  CodedFields,
  CodeCaseList,
  CodeCaseQuery,
  CodeKind,
  EpisodeContext,
  PatientContext,
  MetricsTrend,
  MyDashboard,
  OrgDashboard,
  TrendBucket,
  AuditQueue,
} from "../types";

// --- wire → view mappers -----------------------------------------------------

type WireTimestamp = { seconds: bigint; nanos: number } | undefined;

/**
 * A wire timestamp as epoch milliseconds, or 0 when absent.
 *
 * Carried alongside the formatted string because the batch list ORDERS on this
 * and DISPLAYS the other. It used to order on the formatted string, which is
 * locale-formatted ("8/7/2026") and sorts lexically after "8/18/2026".
 *
 * 0 for an unset timestamp, which sorts last under newest-first — the right
 * place for a batch whose date we do not know.
 */
function tsToEpoch(ts: WireTimestamp): number {
  if (!ts) return 0;
  const seconds = Number(ts.seconds);
  if (!Number.isFinite(seconds) || seconds <= 0) return 0;
  return seconds * 1000;
}

/**
 * A wire timestamp as a full ISO 8601 string, or "" when absent/zero.
 *
 * Unlike `tsToDateString`, this is not for display — the code-cases CSV
 * export needs an unambiguous, unzoned form, and the drill-in table formats
 * for display separately from this value.
 */
function tsToISO(ts: WireTimestamp): string {
  const ms = tsToEpoch(ts);
  return ms > 0 ? new Date(ms).toISOString() : "";
}

function tsToDateString(ts: WireTimestamp): string {
  if (!ts) return "";
  // A zero/unset protobuf timestamp is the UNIX epoch's predecessor, not a
  // date. Rendering it yields "1/1/1", which reads as a real (absurd) date
  // rather than as missing data — and every FA-fed case has one today, because
  // Case.ReceivedAt is only ever set by the sftp.upload leg.
  const seconds = Number(ts.seconds);
  if (!Number.isFinite(seconds) || seconds <= 0) return "";
  return new Date(seconds * 1000).toLocaleDateString();
}

/** An empty coded result — used for a blank A/B slot (e.g. the B side pre-FA). */
function emptyCoded(): CodedFields {
  return {
    surgical_cpt: "",
    anesthesia_cpt: "",
    primary_icd: "",
    additional_icd: [],
    modifiers: [],
    phys_status: "",
    emergency: false,
    unusual_position: false,
  };
}

// No `ancillary` here, and none on the wire either — the field was withdrawn
// from evalreview.v1.CodedResult (reserved 5). Only the agent leg could produce
// it, so the slot holding ancillary codes was always the agent. See ../types.ts.
interface WireEpisode {
  facilityId: string;
  facilityName: string;
  serviceStartDate: string;
  serviceEndDate: string;
  placeOfService: string;
  operatingRoom: string;
  anesthesiaType: string;
  providerIdOfRecord: string;
  crnaIdOfRecord: string;
  coderId: string;
  codedWhen: string;
  prStatus: string;
  cdStatus: string;
  peStatus: string;
  coderName: string;
}

interface WirePatient {
  lastName: string;
  mrn: string;
  gender: string;
  accountNumber: string;
  payer: string;
  payerPlan: string;
}

interface WireCodedResult {
  surgicalCpt: string;
  anesthesiaCpt: string;
  primaryIcd: string;
  additionalIcd: string[];
  modifiers: string[];
  physStatus: string;
  emergency: boolean;
  unusualPosition: boolean;
}

/**
 * Maps one leg's wire result into the view's fields.
 *
 * The procedure codes are stripped of FA's local annotation HERE, at the one
 * boundary every case takes into the view, rather than at each render site.
 * There are a dozen places a code reaches the screen — chips, the union grid,
 * the codebook modal, aria labels — and a hide that misses one is not a hide:
 * a single annotated chip identifies the coder's slot, and the A/B arrangement
 * is stable per case, so it identifies it for every case afterwards.
 *
 * Only procedure codes are touched. The pattern is five digits then letters,
 * which no ICD code, modifier (QK, AA) or physical status (P1-P6) matches.
 *
 * This is a VIEW measure, not a cryptographic one: the raw value still crosses
 * the wire and is visible in devtools. It closes the accidental leak a reviewer
 * cannot help seeing, not a deliberate search.
 */
function toCodedFields(r: WireCodedResult | undefined): CodedFields {
  if (!r) return emptyCoded();
  return {
    surgical_cpt: comparableCode(r.surgicalCpt) ?? "",
    anesthesia_cpt: comparableCode(r.anesthesiaCpt) ?? "",
    primary_icd: r.primaryIcd,
    additional_icd: r.additionalIcd ?? [],
    modifiers: r.modifiers ?? [],
    phys_status: r.physStatus,
    emergency: r.emergency,
    unusual_position: r.unusualPosition,
  };
}

/** review_status → the queue's CaseStatus. */
function toCaseStatus(reviewStatus: string): CaseStatus {
  return reviewStatus === "submitted" ? "submitted" : "unreviewed";
}

/** batch status → the card's lifecycle state. */
function toBatchState(status: string): AuditBatchState {
  if (status === "complete") return "complete";
  if (status === "in_progress") return "active";
  return "new";
}

/**
 * How a case with no demographics is identified on screen.
 *
 * NOT the DCN. This used to be `patient: c.id`, which put a 15-digit document
 * control number in the slot labelled "patient" — and a reviewer reading a
 * number under a patient heading has no way to know it is not one. Worse, the
 * DCN is scoped per FA job and collides across clients (~2% of episodes,
 * HUB-633), so the same "patient" appears on unrelated cases.
 *
 * The DCN is still shown — as the case id, which is what it is. This is the
 * separate question of who the case is about, and until the FA case-context leg
 * is served the honest answer is that we do not know yet.
 */
const UNKNOWN_PATIENT = "—";

/** An empty clinical record — M1 does not extract demographics from the PDF. */
function emptyRecord(): CaseRecord {
  return {
    indication: "",
    procedure: "",
    findings: "",
    comorbidities: [],
    airway: "",
    asaObserved: "",
    technique: "",
    agents: "",
    anesStart: "",
    anesStop: "",
    surgStart: "",
    surgStop: "",
  };
}

/**
 * FA's view of how far the batch's coding has got.
 *
 * `uncoded` is derived here rather than carried: FA evaluates active and coded
 * as separate subqueries against a moving table, so coded can briefly exceed
 * active, and a negative count on the card would read as a fault in the screen
 * rather than a race in the source.
 */
function codingState(b: {
  activeEpisodes: number;
  codedEpisodes: number;
  inactiveEpisodes: number;
  codingComplete: boolean;
  declaredCount: number;
  codingStatus: string;
  closeDate?: WireTimestamp;
  qcDueDate?: WireTimestamp;
}): Pick<
  AuditBatch,
  | "activeEpisodes"
  | "codedEpisodes"
  | "uncodedEpisodes"
  | "inactiveEpisodes"
  | "codingComplete"
  | "declaredCount"
  | "codingStatus"
  | "closeDate"
  | "qcDueDate"
  | "closeDateSort"
  | "qcDueDateSort"
> {
  return {
    closeDate: tsToDateString(b.closeDate),
    qcDueDate: tsToDateString(b.qcDueDate),
    closeDateSort: tsToEpoch(b.closeDate),
    qcDueDateSort: tsToEpoch(b.qcDueDate),
    activeEpisodes: b.activeEpisodes,
    codedEpisodes: b.codedEpisodes,
    uncodedEpisodes: Math.max(0, b.activeEpisodes - b.codedEpisodes),
    inactiveEpisodes: b.inactiveEpisodes,
    codingComplete: b.codingComplete,
    declaredCount: b.declaredCount,
    codingStatus: b.codingStatus,
  };
}

/** Wire → view for the episode's non-PHI context. */
function toEpisode(e: WireEpisode | undefined): EpisodeContext | undefined {
  if (!e) return undefined;
  return {
    facilityId: e.facilityId,
    facilityName: e.facilityName ?? "",
    serviceStartDate: e.serviceStartDate,
    serviceEndDate: e.serviceEndDate,
    placeOfService: e.placeOfService,
    operatingRoom: e.operatingRoom,
    anesthesiaType: e.anesthesiaType,
    providerIdOfRecord: e.providerIdOfRecord,
    crnaIdOfRecord: e.crnaIdOfRecord,
    coderId: e.coderId,
    coderName: e.coderName ?? "",
    codedWhen: e.codedWhen,
    prStatus: e.prStatus,
    cdStatus: e.cdStatus,
    peStatus: e.peStatus,
  };
}

/**
 * Wire → view for the PHI half.
 *
 * undefined when the server sent none, which means the caller's grant does not
 * confer PHI. Deliberately NOT coerced to an empty object: the views branch on
 * presence to say "restricted" rather than "unknown", and an empty object would
 * collapse that distinction at the seam.
 */
function toPatient(p: WirePatient | undefined): PatientContext | undefined {
  if (!p) return undefined;
  return {
    lastName: p.lastName,
    mrn: p.mrn,
    gender: p.gender,
    accountNumber: p.accountNumber,
    payer: p.payer,
    payerPlan: p.payerPlan,
  };
}

// --- source ------------------------------------------------------------------

export const liveAuditSource: AuditDataSource = {
  // The reviewer queues, verbatim. No blinding applies: a queue names batches
  // and principals, never a coded value or which slot is the agent.
  async fetchQueues(): Promise<AuditQueue[]> {
    const res = await evalReviewService.listQueues({});
    return (res.queues ?? []).map(
      (q): AuditQueue => ({
        id: q.id,
        name: q.name || q.id,
        batchIds: [...(q.batchIds ?? [])],
        reviewerIds: [...(q.reviewerIds ?? [])],
        reviewers: (q.reviewers ?? []).map((r) => ({
          id: r.id,
          displayName: r.displayName,
        })),
      }),
    );
  },

  async fetchBatches(): Promise<AuditBatch[]> {
    const res = await evalReviewService.listBatches({});
    // The queue cards need counts, not the per-case list — keep this cheap and
    // fill `cases` on demand in fetchBatch.
    return (res.batches ?? []).map((b): AuditBatch => {
      const caseCount = b.caseCount;
      const reviewedCount = b.reviewedCount;
      return {
        id: b.id,
        // FA's OWN batch code, not the composed label.
        //
        // `label` is "ACME · B-118" — the client concatenated with the code —
        // and the card then rendered the client again and the job code as a
        // chip, so a single row carried the same client three times. Taking the
        // code alone lets each part appear once. Falls back to the label and
        // then the id for ingest-day buckets, which have no FA batch record.
        batchNumber: b.batchCode || b.label || b.id,
        batchCode: b.batchCode,
        dos: tsToDateString(b.receivedAt),
        dosSort: tsToEpoch(b.receivedAt),
        client: b.client,
        jobCode: b.jobCode,
        caseCount,
        reviewedCount,
        remainingCount: Math.max(0, caseCount - reviewedCount),
        discrepancyCaseCount: b.discrepancyCaseCount,
        comparableCaseCount: b.comparableCaseCount,
        // Real FA coder ids (CHANGEDBY_ID), server-side from the batch's cases.
        // This was hardcoded [], which is why the coder column was empty on
        // every row while 193 distinct coders sat in the data.
        coders: b.coderIds ?? [],
        coderNames: b.coderNames ?? [],
        // Per-coder case counts, positionally aligned with `coders`. What lets
        // group-by-coder attribute a batch to whoever did most of it instead of
        // listing it under every coder and printing its total once per group.
        coderCaseCounts: b.coderCaseCounts ?? [],
        faStatus: b.faStatus,
        state: toBatchState(b.status),
        ...codingState(b),
        cases: [],
      };
    });
  },

  async fetchBatch(id: string): Promise<AuditBatch | null> {
    const [batchesRes, casesRes] = await Promise.all([
      evalReviewService.listBatches({}),
      evalReviewService.listCases({ batchId: id }),
    ]);
    const meta = (batchesRes.batches ?? []).find((b) => b.id === id);
    if (!meta) return null;

    const cases: AuditBatchCaseSummary[] = (casesRes.cases ?? []).map((c) => {
      const episode = toEpisode(c.episode);
      const patient = toPatient(c.patient);
      return {
        id: c.id,
        // FA's episode id, which is what the case table LABELS the case with.
        // `id` stays the DCN because that is what the case is fetched and
        // routed by. Empty for a PDF-only case — caseLabel() falls back.
        episodeId: c.episodeId ?? "",
        // The patient's name when the grant confers it, and the SENTINEL
        // otherwise — not an empty string. "Restricted" and "we do not know"
        // are different answers, and a reviewer who cannot tell them apart
        // routes a case that was never incomplete.
        patient: patient?.lastName || UNKNOWN_PATIENT,
        patientFull: patient?.lastName || UNKNOWN_PATIENT,
        mrn: patient?.mrn ?? "",
        payer: patient?.payer ?? "",
        // FA carries no procedure narrative — see EpisodeContext.
        procedure: "",
        status: toCaseStatus(c.reviewStatus),
        coderId: episode?.coderId ?? "",
        episode,
        patient_context: patient,
        discrepancyCount: c.discrepancyCount,
        // Both sides present. Server-derived rather than trusted from the
        // count, because a count of zero is ambiguous on its own.
        comparable: c.hasA && c.hasB,
        comparedFieldCount: c.comparedFieldCount,
        surgical: { a: c.surgicalA, b: c.surgicalB },
      };
    });

    return {
      id: meta.id,
      batchNumber: meta.batchCode || meta.label || meta.id,
      batchCode: meta.batchCode,
      dos: tsToDateString(meta.receivedAt),
      dosSort: tsToEpoch(meta.receivedAt),
      client: meta.client,
      jobCode: meta.jobCode,
      caseCount: meta.caseCount,
      reviewedCount: meta.reviewedCount,
      remainingCount: Math.max(0, meta.caseCount - meta.reviewedCount),
      discrepancyCaseCount: meta.discrepancyCaseCount,
      comparableCaseCount: meta.comparableCaseCount,
      coders: meta.coderIds ?? [],
      coderNames: meta.coderNames ?? [],
      coderCaseCounts: meta.coderCaseCounts ?? [],
      faStatus: meta.faStatus,
      state: toBatchState(meta.status),
      ...codingState(meta),
      cases,
    };
  },

  async fetchCase(id: string): Promise<AuditCase | null> {
    const res = await evalReviewService.getCase({ id });
    const c = res.case;
    if (!c) return null;
    const episode = toEpisode(c.episode);
    const patient = toPatient(c.patient);
    return {
      id: c.id,
      // FA's episode id — what the review header labels the case with. The DCN
      // stays on `id` and the header shows it separately, so a reviewer has both
      // the number they can look up and the one the system correlates on.
      episodeId: c.episodeId ?? "",
      batchId: c.batchId,
      // Empty when the caller's grant withholds PHI. NOT the em-dash sentinel:
      // the header distinguishes "restricted" from "unknown", and baking a
      // dash in here collapses that at the seam.
      patientFull: patient?.lastName ?? "",
      patient: patient?.lastName ?? "",
      mrn: patient?.mrn ?? "",
      // DOB and age stay EMPTY, always. FA redacts the birth date to a
      // year-shifted value and the server has no field for it, so there is
      // nothing to map — and an age computed from a shifted year is a
      // plausible number that is simply false on the screen where age changes
      // the correct code.
      dob: "",
      ageYrs: 0,
      sex: patient?.gender ?? "",
      // Date of SERVICE where FA gives one. receivedAt is when the row reached
      // us, which is not what a header labelled DOS means.
      //
      // FORMATTED here. FA's date columns are strings and arrive as ISO
      // timestamps ("2026-08-04T00:00:00.000Z"), which is what the header was
      // rendering verbatim. faDate reads the day out of the string rather than
      // constructing a Date — FA stamps midnight UTC, and a local-time
      // conversion moves the service date back a day in every US zone.
      dos: faDate(episode?.serviceStartDate) || tsToDateString(c.receivedAt),
      // The NAME where FA has one, the id where it does not — the id is
      // still on `episode` so the view can mark which it is showing.
      facility: episode?.facilityName || episode?.facilityId || "",
      // FA's flattened episode query carries the anesthesia provider and CRNA
      // of record; there is no surgeon column. Left empty rather than filled
      // with the nearest-looking id.
      surgeon: "",
      anesth: [episode?.anesthesiaType, episode?.providerIdOfRecord]
        .filter(Boolean)
        .join(" · "),
      payer: patient?.payer ?? "",
      service: patient?.payerPlan ?? "",
      priority: "",
      status: toCaseStatus(c.reviewStatus),
      receivedAgo: tsToDateString(c.receivedAt),
      // Client belongs to the BATCH, and the compare screen already loads it —
      // see CompareBody's useAuditBatch. Filling it from a second lookup here
      // would be a redundant round trip on the case-open path.
      client: "",
      jobCode: "",
      coderId: episode?.coderId ?? "",
      episode,
      patient_context: patient,
      record: emptyRecord(),
      // The real uploaded PDF for this case (HUB-509). Empty when the case has
      // no stored document.
      documentBlobId: c.documentBlobId ?? "",
      // Already blind: `a` and `b` are opaque slots from the server.
      results: {
        a: toCodedFields(c.a),
        b: toCodedFields(c.b),
      },
      // Whether each slot exists, which an empty CodedFields cannot express.
      hasA: c.hasA,
      hasB: c.hasB,
    };
  },

  async searchCodebook(query: string): Promise<CodebookHit[]> {
    const res = await evalReviewService.searchCodebook({ query, kind: "" });
    return (res.entries ?? []).map((e) => ({
      code: e.code,
      kind: (e.kind as CodeKind) || "",
      title: e.title,
      desc: e.description,
    }));
  },

  /**
   * Record a completed review.
   *
   * Two things this must not do, both of which it used to. It must not drop the
   * reviewer's notes (`note: ''` was hardcoded while the field exists on the
   * wire), and it must not treat a response as success without reading it:
   * `accepted` is the server saying the event was appended, and a client that
   * ignores it cannot distinguish a recorded review from a lost one. Rejection
   * throws so the caller can keep the case open — see auditCompare's finalize.
   */
  async submitAudit(submission: AuditSubmission): Promise<void> {
    const fieldVerdicts = Object.entries(submission.fieldVerdicts ?? {}).map(
      ([field, v]) => ({
        field,
        verdict: v.verdict,
        reasons: v.reasons,
        note: v.note ?? "",
        codes: (v.codes ?? []).map((c) => ({
          code: c.code,
          correct: c.correct,
          reason: c.reason ?? "",
          note: c.note ?? "",
          added: c.added ?? false,
        })),
      }),
    );
    const res = await evalReviewService.submitReview({
      caseId: submission.caseId,
      disposition: submission.disposition ?? "submitted",
      fieldVerdicts,
      note: submission.note ?? "",
    });
    if (!res.accepted) {
      throw new Error(
        "The server did not accept this review. It has not been recorded.",
      );
    }
  },

  // Program metrics come from the server, which folds reviewer verdicts with
  // provenance already resolved. This client never sees which slot was the
  // agent — only the aggregate, which is safe precisely because it is aggregate.
  async fetchOrgDashboard(): Promise<OrgDashboard> {
    // The zone the server buckets trend days/weeks by. Sent from the browser
    // rather than assumed UTC: a reviewer's "today" bucket must match their
    // own clock, or the most recent trend point looks like it is missing
    // hours of the day that already happened for them.
    const res = await evalReviewService.getProgramMetrics({
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

    // A ranked reason with the codes it was cited against. Shared by
    // top_reasons (study-wide) and each field's top_reason_counts (that
    // field only) — both are the same wire shape.
    const reasonCount = (r: {
      reason: string;
      count: number;
      codes?: { field: string; code: string; count: number }[];
    }) => ({
      reason: r.reason,
      count: r.count,
      codes: (r.codes ?? []).map((c) => ({
        field: c.field,
        code: c.code,
        count: c.count,
      })),
    });

    // Shared by the daily and weekly trend series — both are the same wire
    // shape, bucketed at a different grain.
    const bucket = (b: (typeof res.trendDaily)[number]): TrendBucket => ({
      start: b.start,
      casesReceived: b.casesReceived,
      comparable: b.comparable,
      fullyAgreed: b.fullyAgreed,
      reviewed: b.reviewed,
      measured: b.measured,
      judged: b.judged,
      agentCorrect: b.agentCorrect,
      coderCorrect: b.coderCorrect,
      both: b.both,
      neither: b.neither,
      // `?? []` rather than trusting the field to be present: an older server
      // sends no per-field slices at all, and the chip row must then simply
      // not offer any field rather than throw on the map.
      fields: (b.fields ?? []).map((f) => ({
        field: f.field,
        judged: f.judged,
        agentCorrect: f.agentCorrect,
        coderCorrect: f.coderCorrect,
        both: f.both,
        neither: f.neither,
        agree: f.agree,
        differ: f.differ,
        oneSided: f.oneSided,
        notComparable: f.notComparable,
      })),
    });

    // Mapped BEFORE the hasData branch and returned from both: the received
    // and reviewed-throughput buckets exist before any verdict is submitted,
    // same reasoning as `corpus` below — a trend chart that only fills in
    // once hasData is true shows a flat line through the exact phase it is
    // most useful for.
    const trend: MetricsTrend = {
      daily: (res.trendDaily ?? []).map(bucket),
      weekly: (res.trendWeekly ?? []).map(bucket),
      reviewsDaily: (res.reviewsDaily ?? []).map((b) => ({
        start: b.start,
        reviewsSubmitted: b.reviewsSubmitted,
      })),
      reviewsWeekly: (res.reviewsWeekly ?? []).map((b) => ({
        start: b.start,
        reviewsSubmitted: b.reviewsSubmitted,
      })),
    };

    // Mapped BEFORE the hasData branch and returned from both: corpus
    // agreement needs no reviewer verdicts, and it is the only part of this
    // screen that says anything before the first review is submitted.
    const corpus = {
      cases: res.corpusCases,
      comparable: res.corpusComparable,
      fullyAgreed: res.corpusFullyAgreed,
      exclusions: res.corpusExclusions.map((e) => ({
        reason: e.reason,
        cases: e.cases,
      })),
      fields: res.corpusFields.map((f) => ({
        field: f.field,
        agree: f.agree,
        differ: f.differ,
        oneSided: f.oneSided ?? 0,
        notComparable: f.notComparable,
      })),
      codes: res.corpusCodes.map((c) => ({
        field: c.field,
        code: c.code,
        seen: c.seen,
        matched: c.matched,
        exampleDcns: c.exampleDcns ?? [],
      })),
      codesSuppressed: res.corpusCodesSuppressed,
    };

    // Mapped BEFORE the hasData branch and returned from both, same as
    // `trend` and `corpus` above: the floor itself — whether it is cleared,
    // and the thresholds — is not provenance, so a caller below the floor
    // still needs to know how far it is from being measured.
    const verdictFloor = {
      cleared: res.verdictFloorCleared ?? false,
      minReviews: res.minFloorReviews ?? 3,
      minReviewers: res.minFloorReviewers ?? 2,
    };

    // hasData is the difference between "measured zero" and "nothing measured".
    // Rendering 0% for an unstarted study reads as a failing agent, so the
    // empty case returns rates of -1, which the UI shows as "no data yet"
    // rather than as a score. A plain 0 could not carry that distinction.
    if (!res.hasData) {
      return {
        // res.cases, not 0: hasData can be false either because nothing has
        // been reviewed yet OR because the verdict floor withheld the totals
        // (HUB-812) — in the second case cases carries the real reviewed
        // count, which has no provenance, so the copy can say "N reviewed,
        // withheld" instead of reading as an unstarted study.
        reviewed: res.cases,
        thisWeek: 0,
        judged: 0,
        both: 0,
        neither: 0,
        // excluded is reported even with no scorable data. A study whose key
        // rotated before anyone re-reviewed has hasData=false AND a non-zero
        // exclusion count, and "nothing measured" alone would hide why.
        excluded: res.excluded,
        agreement: -1,
        agentAccuracy: -1,
        coderAccuracy: -1,
        selectionResolved: 0,
        selectionUnresolved: res.selectionUnresolved,
        agentSelectionMatch: -1,
        coderSelectionMatch: -1,
        erroredCodes: [],
        erroredCodesSuppressed: res.erroredCodesSuppressed,
        erroredCodesCapped: res.erroredCodesCapped ?? 0,
        avgReviewMin: "—",
        fields: [],
        topReasons: [],
        // The corpus buckets above exist before any review is submitted, so
        // the trend series is carried here too rather than left empty — an
        // unstarted study still received cases on a schedule worth charting.
        trend,
        corpus,
        verdictFloor,
      };
    }

    return {
      reviewed: res.cases,
      thisWeek: 0,
      judged: res.judged,
      both: res.both,
      neither: res.neither,
      excluded: res.excluded,
      agreement: res.agreement,
      agentAccuracy: res.agentAccuracy,
      coderAccuracy: res.coderAccuracy,
      selectionResolved: res.selectionResolved,
      selectionUnresolved: res.selectionUnresolved,
      // The selection rates carry their OWN not-measured test, and it is not
      // hasData: a study can be full of judgements and still have resolved no
      // selection, if every verdict rejected both sides. Passing the server's
      // 0.0 through in that case would report an agent that never once matched
      // the auditor, which is the same lie as 0% accuracy on an unstarted study.
      agentSelectionMatch:
        res.selectionResolved > 0 ? res.agentSelectionMatch : -1,
      coderSelectionMatch:
        res.selectionResolved > 0 ? res.coderSelectionMatch : -1,
      erroredCodes: res.erroredCodes.map((c) => ({
        code: c.code,
        field: c.field,
        agentWrong: c.agentWrong,
        coderWrong: c.coderWrong,
        wrong: c.wrong,
        reasons: (c.reasons ?? []).map((r) => ({
          reason: r.reason,
          count: r.count,
        })),
        exampleDcns: c.exampleDcns ?? [],
      })),
      erroredCodesSuppressed: res.erroredCodesSuppressed,
      erroredCodesCapped: res.erroredCodesCapped ?? 0,
      avgReviewMin: "—",
      fields: res.fields.map((f) => ({
        field: f.field,
        agentCorrect: f.agentCorrect,
        coderCorrect: f.coderCorrect,
        both: f.both,
        neither: f.neither,
        judged: f.judged,
        topReasonCounts: (f.topReasonCounts ?? []).map(reasonCount),
      })),
      // The server's study-wide ranking, not the per-field top-3s concatenated.
      // That concatenation was ordered by whichever field happened to sort
      // first and could repeat a reason across fields, so it ranked nothing.
      topReasons: res.topReasons.map(reasonCount),
      trend,
      corpus,
      verdictFloor,
    };
  },

  // The drill-in behind a report row. Auditor-only on the server; a denial
  // surfaces through the hook as "denied", never as an empty table.
  async fetchCodeCases(q: CodeCaseQuery): Promise<CodeCaseList> {
    const res = await evalReviewService.listCodeCases({
      mode:
        q.mode === "corpus" ? WireCodeCaseMode.CORPUS : WireCodeCaseMode.ERRORED,
      field: q.field ?? "",
      code: q.code ?? "",
      reason: q.reason ?? "",
    });
    return {
      total: res.total,
      rows: res.cases.map((c) => ({
        id: c.id,
        episodeId: c.episodeId,
        batchId: c.batchId,
        receivedAt: tsToISO(c.receivedAt),
        receivedAtEpoch: tsToEpoch(c.receivedAt),
        reviewSubmittedAt: tsToISO(c.reviewSubmittedAt),
        field: c.field,
        code: c.code,
        side: c.side === "agent" || c.side === "coder" ? c.side : "",
        verdict: c.verdict,
        reasons: c.reasons ?? [],
        matched: c.matched,
      })),
    };
  },

  async fetchMyDashboard(): Promise<MyDashboard> {
    return {
      weekLabel: "",
      reviewedWeek: 0,
      reviewedTarget: 0,
      reviewedLastWeek: 0,
      reviewedToday: 0,
      avgReviewMin: "—",
      avgLastWeek: "—",
      queueRemaining: 0,
      agreementRate: 0,
      submittedTotal: 0,
      dayLabels: [],
      dayCounts: [],
      dayTarget: 0,
      verdictMix: { both: 0, a: 0, b: 0, neither: 0 },
      fieldsFlagged: [],
      topReasons: [],
      recent: [],
      streak: 0,
      rank: 0,
      teamSize: 0,
    };
  },
};
